// Eine Dublettengruppe: die Leads mit gleichem Wert in einer Tabelle. Ein Lead ist als "Behalten" gewählt (Standard: der mit Kundenakte,
// sonst der älteste); jede andere Zeile hat "Zusammenführen …" und "Keine Dublette".
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { dublettenApi, type DubletteLead, type DublettenGruppe, type ZusammenfuehrenErgebnis } from "../api/dubletten";
import { StatusPille } from "../components/Bausteine";
import { useMeldung } from "../components/Meldungen";
import { telefonText } from "../werkzeuge/telefon";
import { tagText, ZusammenfuehrenDialog } from "./ZusammenfuehrenDialog";
import { ART_TITEL, TX } from "./texte";

function startZiel(leads: DubletteLead[]): string {
  return (leads.find((l) => l.hat_kundenakte) ?? leads[0])?.id ?? "";
}

export function gruppenWert(g: DublettenGruppe): string {
  if (g.art === "telefon") return telefonText(g.wert);
  if (g.art === "name") return g.leads[0]?.name ?? g.wert;
  return g.wert;
}

export function GruppeKarte({ gruppe, onGeaendert, onZusammengefuehrt }: {
  gruppe: DublettenGruppe;
  onGeaendert: () => void;
  onZusammengefuehrt: (e: ZusammenfuehrenErgebnis) => void;
}) {
  const meldung = useMeldung();
  const [ziel, setZiel] = useState(() => startZiel(gruppe.leads));
  const [quelle, setQuelle] = useState<string | null>(null);
  const gemerkt = useMemo(() => new Set((gruppe.ignoriert ?? []).map(([a, b]) => (a < b ? `${a}|${b}` : `${b}|${a}`))), [gruppe.ignoriert]);
  const istGemerkt = (a: string, b: string) => gemerkt.has(a < b ? `${a}|${b}` : `${b}|${a}`);
  const zielLead = gruppe.leads.find((l) => l.id === ziel) ?? gruppe.leads[0];
  const quelleLead = gruppe.leads.find((l) => l.id === quelle);

  const merken = async (l: DubletteLead) => {
    try {
      if (istGemerkt(l.id, ziel)) await dublettenApi.ignorierenAufheben(l.id, ziel);
      else await dublettenApi.ignorieren(l.id, ziel);
      onGeaendert();
    } catch (e) {
      meldung.fehler(e, "Nicht gespeichert");
    }
  };

  return (
    <section className={`dub-gruppe${gruppe.ganz_ignoriert ? " dub-gruppe--ignoriert" : ""}`}>
      <header className="dub-gruppe__kopf">
        <span className="dub-gruppe__art">{ART_TITEL[gruppe.art]}</span>
        <span className="dub-gruppe__wert" title={gruppenWert(gruppe)}>
          {gruppenWert(gruppe)}
        </span>
        <span className="dub-gruppe__rechts">
          {gruppe.schwach ? <span className="marke marke--leise" title={TX.schwach}>kurzer Name</span> : null}
          {gruppe.ganz_ignoriert ? <span className="marke marke--leise">{TX.gemerkt}</span> : null}
          <span>{gruppe.leads.length + (gruppe.weitere ?? 0)} Leads</span>
        </span>
      </header>
      <table className="dub-tabelle">
        <colgroup>
          <col className="dub-sp-wahl" />
          <col />
          <col className="dub-sp-status" />
          <col className="dub-sp-datum" />
          <col className="dub-sp-zahl" />
          <col className="dub-sp-zahl" />
          <col className="dub-sp-zahl" />
          <col className="dub-sp-aktion" />
        </colgroup>
        <thead>
          <tr>
            <th>{TX.behalten}</th>
            <th>Lead</th>
            <th>Status</th>
            <th>Angelegt</th>
            <th className="dub-nr" title="Kontakte">Kont.</th>
            <th className="dub-nr" title="Aktivitäten">Akt.</th>
            <th className="dub-nr" title="Offene Aufgaben">Aufg.</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {gruppe.leads.map((l) => {
            const istZiel = l.id === ziel;
            return (
              <tr key={l.id} className={istZiel ? "dub-ziel" : ""}>
                <td>
                  <label className="dub-radio">
                    <input type="radio" name={`ziel-${gruppe.art}-${gruppe.wert}`} checked={istZiel} onChange={() => setZiel(l.id)} aria-label={`${l.name} behalten`} />
                    {istZiel ? <span className="leise">{TX.behalten}</span> : null}
                  </label>
                </td>
                <td>
                  <Link className="dub-name" to={`/lead/${encodeURIComponent(l.id)}`}>
                    {l.name}
                  </Link>{" "}
                  {l.hat_kundenakte ? (
                    <span className="marke marke--leise" title={TX.kundenakteTitel}>
                      {TX.kundenakte}
                    </span>
                  ) : null}
                  <span className="dub-sub">{[l.ort, l.telefon ? telefonText(l.telefon) : null, l.email].filter(Boolean).join(" · ")}</span>
                </td>
                <td>{l.status_label ? <StatusPille label={l.status_label} klein schlicht /> : null}</td>
                <td className="leise">{tagText(l.date_created)}</td>
                <td className="dub-nr">{l.kontakte}</td>
                <td className="dub-nr">{l.aktivitaeten}</td>
                <td className="dub-nr">{l.offene_aufgaben}</td>
                <td className="dub-aktion">
                  {istZiel ? null : (
                    <>
                      <button type="button" className="knopf knopf--klein" onClick={() => setQuelle(l.id)}>
                        {TX.zusammenfuehren}
                      </button>
                      <button type="button" className="knopf knopf--klein knopf--leise" title={istGemerkt(l.id, ziel) ? TX.wiederZeigen : TX.keineDubletteTitel} onClick={() => void merken(l)}>
                        {istGemerkt(l.id, ziel) ? TX.wiederZeigen : TX.keineDublette}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {gruppe.weitere ? <div className="dub-gruppe__fuss">{TX.weitere(gruppe.weitere)}</div> : null}
      {quelleLead && zielLead ? (
        <ZusammenfuehrenDialog
          offen
          ziel={{ id: zielLead.id, name: zielLead.name ?? zielLead.id }}
          quelle={{ id: quelleLead.id, name: quelleLead.name ?? quelleLead.id }}
          onSchliessen={() => setQuelle(null)}
          onFertig={(e) => {
            setQuelle(null);
            onZusammengefuehrt(e);
          }}
        />
      ) : null}
    </section>
  );
}
